/**
 * 018: COPIED from
 *   vendor/isomorphic/apps/isomorphic/src/app/auth/(sign-in)/sign-in-1/{page,sign-in-form}.tsx
 *   wrapper: vendor/isomorphic/apps/isomorphic/src/app/shared/auth-layout/auth-wrapper-one.tsx
 *   flourish: vendor/isomorphic/packages/isomorphic-core/src/components/shape/underline.tsx
 *
 * `sign-in-1`, not `auth-wrapper-three`'s bare card — the wrapper is the template's own file,
 * imported from @steelyard/theme, not re-drawn, banner column and all. The form below is
 * `sign-in-1/sign-in-form.tsx`'s markup - the same `Form`, the same rizzui `Input`/`Password`/
 * `Checkbox`/`Button`, the same `size="lg"`, the same `className="[&>label>span]:font-medium"`
 * and `inputClassName="text-sm"` on every field, the same arrow on the submit. Only what it DOES
 * is ours — and its spacing, tightened (`space-y-4`) so the page fits one screen with the
 * Turnstile check.
 *
 * The hero image (`sign-in-1/page.tsx`'s `pageImage`) is local, at
 * `src/assets/auth/sign-in-hero.svg`, rather than pointing at the template's S3 bucket — the gate
 * runs offline, and a remote URL is a blank half-screen with no backend and a failed page in CI.
 * It is also **our own artwork now**: the file downloaded from that bucket was the vendor's
 * marketing screenshot, carrying isomorphic's logo and wordmark and a fabricated "$12,402 /
 * +32.40% Increased last month" tile — the wrong brand and an invented figure, on the first
 * screen anyone sees. The replacement is a section drawing of the stock this distributor sells,
 * in the same draughting language as `public/steel-sections.svg`. All five auth screens import
 * it, so it is one file, not five.
 *
 * `isSocialLoginActive` is the one prop that differs from the template's page: `false`,
 * because this product has no social login. The wrapper already hides that block on `false`.
 *
 * What is ours, and why:
 *  - `signIn(email, password)` instead of next-auth's `signIn('credentials', ...)`.
 *  - the refusal wording and FR-016's "only the status travels" rule.
 *  - `returnTo`, and the post-reset confirmation.
 *  - "Forgot password?" points at /password-reset; the template's "Sign Up" line is dropped,
 *    because a back office has no self-service sign-up.
 *  - the wrapper's social-login slot — a full-width button above the template's own
 *    `OrSeparation` — holds "Sign in with a one-time code" instead: the primary sign-in for
 *    warehouse staff, who have no password (passwordless roles). The email+password form below
 *    the separator is the administrators' option.
 *  - `defaultValues` are empty. The template ships admin@admin.com/admin prefilled.
 *  - the template's "Remember Me" checkbox is dropped. `loginSchema` (copied, frozen) already
 *    types `rememberMe` as optional, so leaving it out of the form costs nothing there — but
 *    nothing downstream ever read it: `signIn()` takes only email/password, there's no third
 *    argument anywhere in the identity OpenAPI document, and the BFF's session cookies carry one
 *    fixed lifetime regardless. A checkbox with no effect is worse than no checkbox — it promises
 *    a longer session that never comes.
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { SubmitHandler } from "react-hook-form";
import { PiArrowRightBold } from "react-icons/pi";
import { Password, Button, Input, Text } from "rizzui";
import {
  AuthWrapperOne,
  UnderlineShape,
  Form,
  FieldError,
  OrSeparation,
  loginSchema,
  type LoginSchema,
} from "@steelyard/theme";

import { landingAfterSignIn, signIn, safeReturnTo } from "../../lib/identity";
import { useTurnstile } from "../../components/turnstile";
import heroImage from "../../assets/auth/sign-in-hero.svg";

/**
 * Ported from apps/admin/app/(auth)/sign-in/page.tsx + sign-in-form.tsx.
 *
 * The Next version needed no client runtime at all — a native <form action={serverAction}>. An
 * SPA has no such thing; this is a normal controlled submit with local pending/refusal state,
 * but the wording and the "only the status travels" rule (FR-016 — never the email, password, or
 * six-refusals-worded-apart problem document) are preserved exactly.
 */
const SIGN_IN_REFUSED =
  "We could not sign you in with those details. Check the email address and password, and if the " +
  "problem continues, ask an administrator to look at your account.";
const SIGN_IN_THROTTLED =
  "Too many sign-in attempts have come from this connection. Wait a few minutes and try again.";

function refusalText(status: number): string | null {
  if (status === 429) return SIGN_IN_THROTTLED;
  if (status === 401) return SIGN_IN_REFUSED;
  return null;
}

export default function SignInPage() {
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get("returnTo"));
  const justReset = searchParams.get("reset") === "done";
  // Set by rootLoader after ending a session that lacks backoffice.access (spec §5 "Surface").
  const denied = searchParams.get("denied") === "backoffice";
  const otpHref = returnTo === "/" ? "/otp" : `/otp?${new URLSearchParams({ returnTo })}`;

  const [refused, setRefused] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);
  const captcha = useTurnstile("sign_in");

  const onSubmit: SubmitHandler<LoginSchema> = async (data) => {
    setRefused(null);
    setSubmitError(null);
    setPending(true);
    try {
      const { refused: status } = await signIn(data.email, data.password, await captcha.getToken());
      if (status) return setRefused(status);
      // A full navigation, not `navigate()` — the same reason `signOut()` uses
      // `window.location.assign` in lib/identity.ts rather than a client-side transition: it
      // guarantees `rootLoader` runs fresh against the cookies this response just set, rather than
      // depending on react-router to notice a same-path or already-in-flight navigation needs a
      // new loader pass (observed intermittently: a 200 that left the SPA still parked on this form).
      window.location.assign(await landingAfterSignIn(returnTo));
    } catch (err) {
      setSubmitError(err);
    } finally {
      setPending(false);
      captcha.reset(); // single-use: a retry needs a fresh token
    }
  };

  return (
    <AuthWrapperOne
      title={
        <>
          Welcome back —{" "}
          <span className="relative inline-block">
            sign in
            <UnderlineShape className="absolute -bottom-2 start-0 h-2.5 w-24 text-blue md:w-28 xl:-bottom-1.5 xl:w-36" />
          </span>{" "}
          to the back office.
        </>
      }
      description="Orders, catalogue, invoicing and Tally sync — everything the Infra Junction back office runs on, in one place."
      bannerTitle="Infra Junction's operations, in one back office."
      bannerDescription="Orders, credit, GST compliance and warehouse dispatch — the same system your colleagues use, kept current with what the API actually returns."
      isSocialLoginActive={false}
      pageImage={
        <div className="relative mx-auto aspect-[4/3.37] h-[calc(100vh-31rem)] max-w-full">
          <img src={heroImage} alt="" className="absolute inset-0 size-full rounded-lg object-contain" />
        </div>
      }
    >
      {justReset ? (
        <Text role="status" className="mb-5 text-sm font-medium text-green-dark">
          Your password has been changed. Sign in with the new one.
        </Text>
      ) : null}

      {denied && !refused && !submitError ? (
        <div className="mb-5">
          <FieldError
            error={{
              status: 403,
              title: "This account cannot use the back office",
              detail: "You have been signed out. Use the customer portal or the packing app instead.",
            }}
          />
        </div>
      ) : null}

      {refused ? (
        <div className="mb-5">
          <FieldError
            message={refusalText(refused) ?? undefined}
            error={refusalText(refused) ? undefined : { status: refused }}
          />
        </div>
      ) : null}

      {submitError ? (
        <div className="mb-5">
          <FieldError error={submitError} />
        </div>
      ) : null}

      <Link to={otpHref} className="block">
        <Button as="span" className="w-full" size="lg">
          Sign in with a one-time code
        </Button>
      </Link>
      <Text className="mt-2 text-sm text-gray-500">
        Warehouse staff: no password — we send a 6-digit code to your email.
      </Text>
      <OrSeparation title="Administrators: email and password" className="my-4" isCenter />

      <Form<LoginSchema>
        validationSchema={loginSchema}
        onSubmit={onSubmit}
        useFormProps={{ mode: "onChange", defaultValues: { email: "", password: "" } }}
      >
        {({ register, formState: { errors } }) => (
          // Tighter than the template's space-y-5, and "Forgot password?" rides on the Password
          // label's line rather than a row of its own: with the Turnstile check, the page must still
          // fit a 1920×911 viewport without scrolling.
          <div className="space-y-4">
            <Input
              type="email"
              size="lg"
              label="Email"
              placeholder="Enter your email"
              className="[&>label>span]:font-medium"
              inputClassName="text-sm"
              {...register("email")}
              error={errors.email?.message}
            />
            {/* The link sits beside the label, outside it, so the field's accessible name stays "Password". */}
            <div className="relative">
              <Password
                label="Password"
                placeholder="Enter your password"
                size="lg"
                className="[&>label>span]:font-medium"
                inputClassName="text-sm"
                {...register("password")}
                error={errors.password?.message}
              />
              <Link
                to="/password-reset"
                className="absolute end-0 top-0 h-auto p-0 text-sm font-semibold text-blue underline transition-colors hover:text-gray-900 hover:no-underline"
              >
                Forgot password?
              </Link>
            </div>
            {captcha.widget}
            <Button className="w-full" type="submit" size="lg" variant="outline" isLoading={pending}>
              <span>Sign in with password</span> <PiArrowRightBold className="ms-2 mt-0.5 h-6 w-6" />
            </Button>
          </div>
        )}
      </Form>
    </AuthWrapperOne>
  );
}
